import {
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from "react-native";
import { useMemo } from "react";
import { DataTable } from "react-native-paper";
import { useTranslation } from "react-i18next";
import { ValueDisplayTable } from "../../../components/ValueDisplayTable";
import TableTransposedLayout from "../../../components/tableView/TableTransposedLayout";
import { SMALL_SCREEN_WIDTH } from "../../../constants/constants";
import { Field, OrderByItem } from "../../../config/companies/config";
import { compareTableValues } from "../../../utils/tableSort";
import { getNestedValue } from "../../../utils/utils";

interface TableBoardProps {
  fields: Field[];
  sortFields?: Field[];
  navigateToDetails: (card: any) => void;
  rawData: any[];
  orderBy?: OrderByItem[];
  responsiveEnabled?: boolean;
  responsiveBreakpoint?: number;
}

export const TableBoard = ({
  fields,
  sortFields,
  navigateToDetails,
  rawData,
  orderBy,
  responsiveEnabled = false,
  responsiveBreakpoint = SMALL_SCREEN_WIDTH,
}: TableBoardProps) => {
  const { t } = useTranslation();
  const { width: viewportWidth } = useWindowDimensions();
  const fieldsByName = useMemo<Record<string, Field>>(
    () =>
      (sortFields ?? fields).reduce(
        (acc, field) => {
          acc[field.name] = field;
          return acc;
        },
        {} as Record<string, Field>,
      ),
    [fields, sortFields],
  );

  const sortedData = useMemo(() => {
    if (!orderBy?.length) {
      return rawData;
    }

    return [...rawData].sort((a, b) => {
      for (const orderItem of orderBy) {
        const aValue = getNestedValue(a, orderItem.field);
        const bValue = getNestedValue(b, orderItem.field);

        if (aValue == null && bValue != null) return 1;
        if (aValue != null && bValue == null) return -1;
        if (aValue == null && bValue == null) continue;

        const comparison = compareTableValues(
          aValue,
          bValue,
          fieldsByName[orderItem.field],
        );

        if (comparison !== 0) {
          return orderItem.direction === "desc" ? -comparison : comparison;
        }
      }

      return 0;
    });
  }, [fieldsByName, orderBy, rawData]);

  const shouldUseTransposedTable =
    responsiveEnabled && viewportWidth <= responsiveBreakpoint;

  if (shouldUseTransposedTable) {
    return (
      <View style={styles.container}>
        <TableTransposedLayout
          orderBy={undefined}
          sortOptions={{}}
          handleSort={() => {}}
          paginatedData={sortedData}
          fields={fields}
          collection={{}}
          permittedFields={fields}
          getRowStyle={() => ({})}
          navigateToDetailsScreen={(_, data) => navigateToDetails(data)}
          t={t}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView>
        <DataTable>
          <DataTable.Header>
            <DataTable.Title style={[styles.cell, { flex: 0.2 }]}>
              #
            </DataTable.Title>
            {fields.map((field) => (
              <DataTable.Title
                key={field.name}
                style={[styles.cell, field.width ? { flex: field.width } : {}]}
              >
                {t(field.label || field.name)}
              </DataTable.Title>
            ))}
          </DataTable.Header>

          {sortedData.map((item, index) => (
            <DataTable.Row
              key={item.id}
              onPress={() => navigateToDetails(item)}
              style={styles.row}
            >
              <DataTable.Cell style={[styles.cell, { flex: 0.2 }]}>
                {sortedData.length - index}
              </DataTable.Cell>
              {fields.map((field) => (
                <DataTable.Cell
                  key={field.name}
                  style={[
                    styles.cell,
                    field.width ? { flex: field.width } : {},
                  ]}
                >
                  <ValueDisplayTable field={field} value={item[field.name]} />
                </DataTable.Cell>
              ))}
            </DataTable.Row>
          ))}
        </DataTable>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
  },
  row: {
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  cell: {
    padding: 8,
  },
});
